import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Home,
  Users,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  Boxes,
  DollarSign,
  CalendarClock,
  ArrowRight,
  Gift,
  Copy,
} from "lucide-react";

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const statusColor = {
  scheduled: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6)));
    api.get("/inventory/alerts").then((r) => setAlerts(r.data));
    api.get("/referrals").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const siteUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const referralLink = referral
    ? `${siteUrl}/signup?ref=${referral.code}`
    : "";

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied!");
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const cards = [
    { label: "Properties", value: stats?.total_properties, icon: Home, accent: "text-primary" },
    { label: "Cleaners", value: stats?.total_cleaners, icon: Users, accent: "text-sky-600" },
    { label: "Upcoming Jobs", value: stats?.upcoming_jobs, icon: CalendarClock, accent: "text-violet-600" },
    { label: "Completed", value: stats?.completed_jobs, icon: CheckCircle2, accent: "text-emerald-600" },
  ];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
          Owner Dashboard
        </p>
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <Card
            key={c.label}
            className="p-5 hover:shadow-md transition-shadow"
            data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}
          >
            <c.icon className={`h-5 w-5 ${c.accent} mb-3`} strokeWidth={1.7} />
            <div className="font-display text-3xl font-semibold">
              {c.value ?? "—"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{c.label}</p>
          </Card>
        ))}
      </div>

      {/* Revenue + alerts bento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 bg-primary text-primary-foreground lg:col-span-1">
          <DollarSign className="h-6 w-6 mb-4 opacity-90" strokeWidth={1.7} />
          <p className="text-sm opacity-80">Revenue (completed cleanings)</p>
          <div className="font-display text-4xl font-semibold mt-2">
            ${stats?.revenue?.toLocaleString() ?? "0"}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[#DD6B20]" /> Damage reports
            </span>
            <Link to="/owner/photos" className="text-xs text-primary hover:underline">
              Review
            </Link>
          </div>
          <div className="font-display text-4xl font-semibold">
            {stats?.damage_reports ?? 0}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Pre-cleaning damage photos uploaded
          </p>
        </Card>

        <Card className="p-6 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium flex items-center gap-2">
              <Boxes className="h-4 w-4 text-[#DD6B20]" /> Low inventory
            </span>
            <Link to="/owner/inventory" className="text-xs text-primary hover:underline">
              Manage
            </Link>
          </div>
          <div className="font-display text-4xl font-semibold">
            {stats?.low_stock ?? 0}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Items need refilling</p>
        </Card>
      </div>

      {/* Referral card */}
      {referral && (
        <Card className="p-6 mb-6 bg-secondary border-0" data-testid="referral-card">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" /> Invite another cleaning company
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Share your link — when a company you refer subscribes, you get
                {" "}<span className="text-foreground font-medium">50% off your next month</span>.
                {" "}
                <span className="text-foreground font-medium">
                  {referral.signups} signed up · {referral.converted} subscribed
                  {referral.credits > 0 ? ` · ${referral.credits} reward${referral.credits > 1 ? "s" : ""} earned` : ""}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <input
                readOnly
                value={referralLink}
                onFocus={(e) => e.target.select()}
                className="flex-1 lg:w-80 text-sm rounded-lg border border-border bg-background px-3 py-2 text-muted-foreground"
                data-testid="referral-link-input"
              />
              <Button onClick={copyReferral} className="shrink-0" data-testid="copy-referral-button">
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent jobs */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" /> Recent jobs
            </h2>
            <Link
              to="/owner/jobs"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No jobs yet. Add a property and sync its calendar.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {jobs.map((j) => (
                <div
                  key={j.id}
                  className="flex items-center justify-between py-3"
                  data-testid={`dashboard-job-${j.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{j.property_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtDate(j.date)} · {j.cleaners?.length || 0} cleaner(s)
                    </p>
                  </div>
                  <Badge className={`${statusColor[j.status]} border-0 capitalize`}>
                    {j.status.replace("_", " ")}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Inventory alerts */}
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#DD6B20]" /> Refill alerts
          </h2>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              All stocked up 🎉
            </p>
          ) : (
            <div className="space-y-3">
              {alerts.slice(0, 6).map((a) => (
                <div key={a.id} className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.property_name}
                    </p>
                  </div>
                  <Badge variant="destructive" className="shrink-0">
                    {a.quantity} left
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
